import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setCategorizedPosDropdown } from "../../state/slices/activeComponentsSlice";
import useCategorizedPositionService from "../../services/PositionAdmin/categorizedPositionService";
import usePositionService from "../../services/PositionAdmin/positionService";
import { message, Select, Button } from "antd";
import PvTable from "../../components/Table/PvTable";
import PvForm from "../../components/Form/PvForm";
import ConfirmDelete from "../../components/ConfirmDelete/ConfirmDelete";
import Popup from "../../components/Popup";

const ApproverList = ({ approverNames, selectedId }) => {
  const names = approverNames?.[selectedId] || [];

  return (
    <div style={{ marginTop: 12 }}>
      {names.length > 0 ? (
        <>
          <strong>These are the approvers of this position:</strong>
          <ul>
            {names.map((name, idx) => (
              <li key={idx}>{name}</li>
            ))}
          </ul>
        </>
      ) : (
        <em>No employees assigned to this position.</em>
      )}
    </div>
  );
};

const CategorizedPosition = () => {
  const dispatch = useDispatch();
  const { categorizedPosDropdown } = useSelector(
    (state) => state.activeComponentsSlice
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [positionsWithoutApprover, setPositionsWithoutApprover] =
    useState(null);
  const [approverNames, setApproverNames] = useState(null);
  const [tableValues, setTableValues] = useState(null);

  const {
    getSubordinate,
    removeSubordinate,
    addSubordinate,
    response,
    loading,
  } = useCategorizedPositionService();
  const {
    getAllApprovers,
    approverLoading,
    approverResponse,
    getAllApproverlessPosition,
    approverlessLoading,
    approverlessResponse,
  } = usePositionService();

  useEffect(() => {
    getAllApprovers();
    getAllApproverlessPosition();
  }, []);

  useEffect(() => {
    if (!approverResponse?.data?.length) return;

    const mapped = approverResponse.data.map((item) => ({
      value: item.id,
      label: `${item?.position_name} - ${item?.unit}`,
    }));
    setOptions(mapped);

    const approverMap = approverResponse.data.reduce((acc, item) => {
      acc[item.id] =
        item.employees?.map((e) =>
          [e.first_name, e.middle_name, e.last_name].filter(Boolean).join(" ")
        ) || [];
      return acc;
    }, {});
    setApproverNames(approverMap);

    if (!categorizedPosDropdown) {
      dispatch(setCategorizedPosDropdown(mapped[0]?.value));
    }
  }, [approverResponse]);

  useEffect(() => {
    if (!approverlessResponse?.data?.length) return;

    const mapped = approverlessResponse.data.map((item) => ({
      name: item.id,
      label: item.position_name,
    }));
    setPositionsWithoutApprover(mapped);
  }, [approverlessResponse]);

  useEffect(() => {
    if (categorizedPosDropdown) {
      getSubordinate(categorizedPosDropdown);
    }
  }, [categorizedPosDropdown]);

  useEffect(() => {
    if (!Array.isArray(response?.data)) return;

    const mapped = response.data.map((item) => ({
      ...item,
      position_name: item.position_name,
      is_approver: item?.is_approver ? "Approver" : "Employee",
    }));
    setTableValues(mapped);
  }, [response]);

  const column = [
    {
      title: "Position Name",
      dataIndex: "position_name",
      key: "position_name",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
    },
    {
      title: "Type",
      dataIndex: "is_approver",
      key: "is_approver",
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => (
        <ConfirmDelete
          isService={true}
          buttonText="Remove"
          onDeleted={() => {
            handleDelete(item?.id);
          }}
        />
      ),
    },
  ];

  const formFields = useMemo(
    () => [
      {
        name: "position_ids",
        label: "Positions",
        type: "multiple-select",
        selectItems: positionsWithoutApprover,
        rules: [
          { required: true, message: "Please select at least one skill." },
        ],
      },
    ],
    [positionsWithoutApprover]
  );

  const handleDelete = async (id) => {
    try {
      await removeSubordinate(id);
      message.success("Position removed successfully.");
      getSubordinate(categorizedPosDropdown);
      getAllApproverlessPosition();
    } catch {
      message.error("Failed to remove position.");
    }
  };

  const handleOnChange = (value) => {
    dispatch(setCategorizedPosDropdown(value));
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Select
        options={options}
        loading={approverLoading}
        onChange={handleOnChange}
        value={categorizedPosDropdown}
        placeholder="Select approver"
        popupMatchSelectWidth={false}
      />

      <Button
        size="large"
        style={{ background: "#00573F" }}
        // icon={<PlusCircleOutlined />}
        type="primary"
        onClick={() => {
          setIsModalOpen(true);
        }}
      >
        Add
      </Button>

      <ApproverList
        approverNames={approverNames}
        selectedId={categorizedPosDropdown}
      />

      <PvTable
        style={{ marginTop: "12px" }}
        data={tableValues ? tableValues : []}
        columns={column}
        loading={loading}
        onRowClick={() => {}}
      />

      <Popup
        title={"Select positions to add to the selected approver"}
        isOpen={isModalOpen}
        footer={null}
        onCancel={handleModalCancel}
      >
        <PvForm
          isService={true}
          formFields={formFields}
          onSubmitted={async (values) => {
            const positionIds = values?.position_ids;
            try {
              await addSubordinate(categorizedPosDropdown, positionIds);
              message.success("Position(s) added successfully.");
              setIsModalOpen(false);
              getSubordinate(categorizedPosDropdown);
              await getAllApproverlessPosition();
            } catch (error) {
              message.error("Failed to add position(s).");
            }
          }}
        />
      </Popup>
    </>
  );
};

export default CategorizedPosition;
